import React from "react";

import {
  faChevronRight,
  faCog,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import { Link } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { useFlow } from "src/api/queries";
import { Pill } from "src/base-components/Pill";
import { Icon, IconButton, IconLink } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { useCapabilities } from "src/hooks/useCapabilities";
import { AppHeaderLogo } from "src/layout/AppHeaderLogo";
import { UserMenu } from "src/layout/UserMenuV2";
import { useOmniboxActions } from "src/omnibox/OmniboxStore";
import {
  DashboardPageParams,
  FlowPageParams,
  getUrlToSettingsPage,
  getUrlToWsDashboard,
} from "src/router/urls";
import { canAccessSettingsPage } from "src/settings/utils";
import { modifierKey } from "src/utils/platformModifierKey";
import { useParamsDecode } from "src/utils/useParamsDecode";

type BreadcrumbProps =
  | {
      label: string;
      href: string;
      dataLoc?: string;
      children?: never;
    }
  | {
      label?: never;
      href?: never;
      dataLoc?: never;
      children: React.ReactNode;
    };

type BreadcrumbType = React.FC<BreadcrumbProps>;

type Props = {
  breadcrumbs?: { key: string; breadcrumb: React.ReactNode }[];
  children?: React.ReactNode;
  border?: boolean;
};

type HeaderType = React.FC<Props> & {
  Breadcrumb: BreadcrumbType;
};

export const LayoutHeader: HeaderType = ({
  border = false,
  breadcrumbs,
  children,
}) => {
  const { wsId, orgId } = useParamsDecode<DashboardPageParams>();
  // We might not be on a flow page, so use a partial decode to get the flow_id
  const { flow_id } = useParamsDecode<Partial<FlowPageParams>>();

  const settingsPageUrl = getUrlToSettingsPage(orgId, wsId);
  const flow = useFlow(flow_id);
  const capabilities = useCapabilities();
  const { showOmnibox } = useOmniboxActions();

  return (
    <header
      className={twJoin(
        "box-border flex w-full items-center justify-between bg-white py-2 pr-3 pl-4",
        border ? "min-h-[53px] border-b border-b-gray-200" : "min-h-[52px]",
      )}
      data-loc="layout-header"
    >
      <div className="flex items-center gap-x-3">
        <Link
          to={getUrlToWsDashboard({
            orgId,
            wsId,
            folderId: flow.data?.flow_folder_id,
          })}
        >
          <AppHeaderLogo dataLoc="header-taktile-logo" />
        </Link>
        {breadcrumbs && breadcrumbs.length > 0 && (
          <ul className="flex items-center gap-x-3" data-loc="breadcrumbs">
            {breadcrumbs.map(({ key, breadcrumb }) => (
              <React.Fragment key={key}>{breadcrumb}</React.Fragment>
            ))}
          </ul>
        )}
      </div>
      <div>
        {children ? (
          children
        ) : (
          <>
            <div className="flex items-center gap-x-4">
              <Tooltip
                placement="bottom-end"
                title={
                  <>
                    <span className="mr-2">Search</span>
                    <Pill size="sm" variant="dark-gray">
                      <Pill.Text fontType="text">{modifierKey} K</Pill.Text>
                    </Pill>
                  </>
                }
                asChild
              >
                <IconButton icon={faSearch} size="xs" onClick={showOmnibox} />
              </Tooltip>
              {canAccessSettingsPage(capabilities) && (
                <Tooltip placement="bottom-end" title="Settings" asChild>
                  <IconLink
                    aria-label="Settings"
                    dataLoc="header-settings-icon"
                    icon={faCog}
                    size="xs"
                    to={settingsPageUrl}
                  />
                </Tooltip>
              )}
              <UserMenu />
            </div>
          </>
        )}
      </div>
    </header>
  );
};

LayoutHeader.Breadcrumb = ({ label, href, children, dataLoc }) => (
  <li className="flex items-center gap-x-3">
    <Icon icon={faChevronRight} size="xs" />
    {href ? (
      <Link
        className="font-inter-semibold-13px text-gray-800"
        data-loc={dataLoc}
        to={href}
      >
        {label}
      </Link>
    ) : (
      children
    )}
  </li>
);
